import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import * as Yup from "yup";
import { motion } from "framer-motion";
import logo from "@/assets/images/logo.png";
import { authService } from "@/services/authService";

export function OtpVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(3);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(30);
  const [otpExpired, setOtpExpired] = useState(false);
  const [error, setError] = useState("");
  
  const inputRefs = useRef([]);
  
  // Handle countdown for resend button
  useEffect(() => {
    let resendTimer;
    if (resendDisabled && resendCountdown > 0) {
      resendTimer = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setResendDisabled(false);
    }
    
    return () => clearInterval(resendTimer);
  }, [resendDisabled, resendCountdown]);
  
  // Handle OTP expiration (10 minutes)
  useEffect(() => {
    const expiryTimer = setTimeout(() => {
      setOtpExpired(true);
      setError("OTP expired. Please sign in again.");
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => clearTimeout(expiryTimer);
  }, []);
  
  const handleChange = (index, e) => {
    const value = e.target.value;
    
    // Only allow digits
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Clear error when user starts typing again
      if (error) setError("");
      
      // Auto-focus next input if value is entered
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };
  
  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        // If current field is empty and backspace is pressed, move to previous field
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
      }
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Check if pasted content is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      
      // Focus the last input
      inputRefs.current[5].focus();
    }
  };
  
  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    
    if (otpValue.length !== 6) {
      setError("Please enter all 6 digits of your OTP");
      return;
    }
    
    if (otpExpired) {
      setError("OTP expired. Please sign in again.");
      setTimeout(() => navigate("/login"), 3000);
      return;
    }
    
    setIsVerifying(true);
    setError("");
    
    try {
      const response = await authService.verifyOtp({
        email,
        otp: otpValue
      });

      if (!response.data) {
        throw new Error("Invalid response data");
      }

      toast.success("OTP verified successfully!");

      // Check if user role is "User" and has no form submission
      if (response.data.user.roles[0].title === "User" && response.data.form_submission === 0) {
        navigate("/video");
      } else {
        navigate("/");
      }
    } catch (error) {
      const newAttempts = attempts - 1;
      setAttempts(newAttempts);
      
      if (newAttempts <= 0) {
        toast.error("Too many failed attempts");
        setTimeout(() => {
          navigate("/login", { 
            state: { 
              loginError: "Too many failed attempts. Please try again." 
            } 
          });
        }, 2000);
      } else {
        setError(`Incorrect OTP. You have ${newAttempts} ${newAttempts === 1 ? 'attempt' : 'attempts'} remaining.`);
      }
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleResendOtp = async () => {
    if (resendDisabled) return;
    
    try {
      setResendDisabled(true);
      setResendCountdown(30);
      
      setAttempts(3);
      setError("");
      setOtpExpired(false);
      
      await authService.resendOtp(email);
      
      toast.custom((t) => (
        <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          New OTP sent successfully!
        </div>
      ), {
        duration: 2000,
        position: "top-right",
        style: {
          zIndex: 9999,
        }
      });
      
      setTimeout(() => {
        setOtpExpired(true);
        setError("OTP expired. Please sign in again.");
      }, 10 * 60 * 1000);
      
    } catch (error) {
      toast.custom((t) => (
        <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          {error.response?.data?.message || "Failed to resend OTP"}
        </div>
      ), {
        position: "top-right",
        style: {
          zIndex: 9999,
        }
      });
      setResendDisabled(false);
      setResendCountdown(0);
    }
  };
  
  // If there's no email in state, redirect to login
  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);
  
  return (
    <>
      <Toaster
        richColors
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
          duration: 3000,
        }}
      />
      <div className="min-h-screen flex items-center justify-center flex-col px-4 sm:px-6" style={{ backgroundColor: '#F1EBDB' }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full space-y-6 sm:space-y-8 bg-white p-6 sm:p-10 rounded-xl shadow-lg"
        >
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-black">OTP Required</h2>
            <p className="mt-2 text-xs sm:text-sm text-[#F16745] text-center mb-4 sm:mb-8">
              Please enter the 6-digit code sent to your email
            </p>
            {email && (
              <p className="text-center text-xs sm:text-sm text-gray-600">
                Code sent to <span className="font-medium">{email}</span>
              </p>
            )}
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            <div>
              <div className="flex justify-center space-x-1 sm:space-x-4 mb-4">
                {Array(6).fill(0).map((_, index) => (
                  <motion.div 
                    key={index}
                    whileTap={{ scale: 0.95 }}
                    className="w-8 sm:w-10 md:w-12"
                  >
                    <input
                      type="text"
                      maxLength={1}
                      value={otp[index]}
                      onChange={(e) => handleChange(index, e)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : null}
                      ref={(el) => (inputRefs.current[index] = el)}
                      className="w-full h-10 sm:h-12 md:h-14 text-center text-lg sm:text-xl font-bold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745]"
                      autoFocus={index === 0}
                    />
                  </motion.div>
                ))}
              </div>
              
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-red-500 text-xs sm:text-sm mt-2"
                >
                  {error}
                </motion.p>
              )}
            </div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleVerifyOtp}
              disabled={isVerifying || otpExpired}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#F16745] hover:bg-[#F16745]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150"
            >
              {isVerifying ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                "Verify OTP"
              )}
            </motion.button>
            
            <div className="flex justify-center items-center mt-4">
              <button
                onClick={handleResendOtp}
                disabled={resendDisabled || otpExpired}
                className={`text-xs sm:text-sm ${
                  resendDisabled 
                    ? "text-gray-400" 
                    : "text-[#F16745] hover:text-[#F16745]/80"
                } focus:outline-none transition-colors`}
              >
                {resendDisabled 
                  ? `Resend code in ${resendCountdown}s` 
                  : "Resend OTP"}
              </button>
            </div>
            
            <div className="text-center mt-4">
              <button
                onClick={() => navigate("/login")}
                className="text-xs sm:text-sm text-gray-600 hover:text-black transition-colors"
              >
                Return to sign in
              </button>
            </div>
          </div>
        </motion.div>
        
        <div className="mt-6 sm:mt-8 mb-4">
          <img src={logo} alt="PostScrypt Logo" className="h-12 sm:h-16 w-auto" />
        </div>
      </div>
    </>
  );
}